import React, { useEffect } from 'react';
import Drawer from '../components/Drawer';
import Footer from '../components/Footer';
import { useOrganization } from '../context/OrganizationContext';
import { useAuth } from '../context/AuthContext';

const cardStyle = { borderColor: '#f4d6df', background: '#fff' };

export default function OrganizationDashboardPage() {
  const { activeOrg, canViewDashboardCards, canViewAnalytics } = useAuth();
  const { org, stats, loadingOrg, error, fetchOrg, fetchStats } = useOrganization();

  if (!canViewDashboardCards && !canViewAnalytics) {
    const deniedContent = (
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10">
        <div className="rounded-3xl border bg-white p-6 text-center" style={{ borderColor: '#f2d8e0' }}>
          <h1 className="text-2xl font-bold text-slate-900">Organization Dashboard</h1>
          <p className="mt-2 text-sm text-slate-500">You do not have permission to view organization dashboard widgets.</p>
        </div>
      </div>
    );

    return (
      <>
        <Drawer pageHeader={deniedContent} />
        <Footer />
      </>
    );
  }

  useEffect(() => {
    if (activeOrg?.id) {
      fetchOrg(activeOrg.id);
      fetchStats(activeOrg.id);
    }
  }, [activeOrg?.id, fetchOrg, fetchStats]);

  const pageContent = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 pb-28 space-y-6">
      <section className="rounded-3xl border p-6 shadow-sm" style={cardStyle}>
        <p className="text-xs uppercase tracking-[0.24em] font-semibold text-slate-400">Tenant Workspace</p>
        <h1 className="text-3xl font-bold text-slate-900 mt-2">{org?.name || activeOrg?.name || 'Organization Dashboard'}</h1>
        <p className="text-sm text-slate-500 mt-2">
          Manage your organization, track tenant health, and monitor shared operational metrics.
        </p>
      </section>

      {error ? (
        <div className="rounded-2xl border px-4 py-3 text-sm" style={{ borderColor: '#f9b8ca', background: '#fce0e8', color: '#c44569' }}>
          {error}
        </div>
      ) : null}

      {canViewDashboardCards ? <section className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {[
          { label: 'Members', value: stats?.total_members ?? '-' },
          { label: 'Projects', value: stats?.total_projects ?? '-' },
          { label: 'Transactions', value: stats?.total_transactions ?? '-' },
          { label: 'Net Balance', value: stats?.net_balance != null ? Number(stats.net_balance).toLocaleString() : '-' },
        ].map((item) => (
          <div key={item.label} className="rounded-3xl border p-5 shadow-sm" style={cardStyle}>
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold">{item.label}</p>
            <p className="mt-3 text-3xl font-bold text-slate-900">{loadingOrg ? '...' : item.value}</p>
          </div>
        ))}
      </section> : null}

      <section className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="rounded-3xl border p-5 shadow-sm" style={cardStyle}>
          <h2 className="text-lg font-bold text-slate-900">Organization Profile</h2>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
            <Info label="Code" value={org?.code || activeOrg?.code || '-'} />
            <Info label="Industry" value={org?.industry || '-'} />
            <Info label="Country" value={org?.country || '-'} />
            <Info label="Currency" value={org?.currency || activeOrg?.currency || '-'} />
            <Info label="Timezone" value={org?.timezone || '-'} />
            <Info label="Plan" value={org?.subscription_plan || '-'} />
          </div>
        </div>

        {canViewAnalytics ? <div className="rounded-3xl border p-5 shadow-sm" style={cardStyle}>
          <h2 className="text-lg font-bold text-slate-900">Finance Snapshot</h2>
          <div className="mt-4 space-y-3 text-sm text-slate-600">
            <div className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: '#f8fafc' }}>
              <span>Total Income</span>
              <strong className="text-emerald-700">{stats?.total_income != null ? Number(stats.total_income).toLocaleString() : '-'}</strong>
            </div>
            <div className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: '#f8fafc' }}>
              <span>Total Expense</span>
              <strong className="text-rose-700">{stats?.total_expenses != null ? Number(stats.total_expenses).toLocaleString() : '-'}</strong>
            </div>
            <div className="flex items-center justify-between rounded-2xl px-4 py-3" style={{ background: '#fff5f8', border: '1px solid #f4d6df' }}>
              <span>Current Net Balance</span>
              <strong style={{ color: '#c44569' }}>{stats?.net_balance != null ? Number(stats.net_balance).toLocaleString() : '-'}</strong>
            </div>
          </div>
        </div> : null}
      </section>
    </div>
  );

  return (
    <>
      <Drawer pageHeader={pageContent} />
      <Footer />
    </>
  );
}

function Info({ label, value }) {
  return (
    <div className="rounded-2xl px-4 py-3" style={{ background: '#f8fafc' }}>
      <p className="text-xs uppercase tracking-[0.18em] text-slate-400 font-semibold">{label}</p>
      <p className="mt-1 font-semibold text-slate-800 break-words">{value}</p>
    </div>
  );
}
